import { Stack, Button, Typography } from "@mui/material";
import {
  ReferenceType,
  ReferenceProjectType,
  Reference,
} from "../../../__generated__/types";
import { DropdownSelect } from "../../../components/dropdown-select";
import { HdgTextInput } from "../../../components/hdg-text-input";
import {
  isEmailValid,
  isPhoneNumberValid,
  ReferenceProjectTypeOptions,
  ReferenceTypeOptions,
} from "../../../utils";
import PlusIcon from "@mui/icons-material/Add";
import { useState } from "react";

export const ReferenceFormComponent = ({
  value,
  onChange,
  currentForm,
  setCurrentForm,
}: {
  value: Reference[];
  onChange: (value: Reference[]) => void;
  currentForm?: Reference | null;
  setCurrentForm?: (value: Reference | null) => void;
}) => {
  const [formInstance, setFormInstance] = useState<number>(0);

  const [referenceType, setReferenceType] = useState<ReferenceType | undefined>(
    currentForm?.type
  );
  const [businessName, setBusinessName] = useState<string>(
    currentForm?.businessName ?? ""
  );
  const [firstName, setFirstname] = useState<string>(
    currentForm?.firstName ?? ""
  );
  const [lastName, setLastName] = useState<string>(
    currentForm?.lastName ?? ""
  );
  const [title, setTitle] = useState<string>(
    currentForm?.title ?? ""
  );
  const [projectType, setProjectType] = useState<
    ReferenceProjectType | undefined
  >(currentForm?.ProjectType ?? undefined);
  const [email, setEmail] = useState<string>(currentForm?.email ?? "");
  const [phoneNumber, setPhoneNumber] = useState<string>(
    currentForm?.phoneNumber ?? ""
  );
  const [startDate, setStartDate] = useState<string>(
    currentForm?.startDate ?? ""
  );
  const [endDate, setEndDate] = useState<string>(
    currentForm?.endDate ?? ""
  );
  const [yearsKnown, setYearsKnown] = useState<string>(
    currentForm?.yearsKnown?.toString() ?? ""
  );

  const normalizeEmail = (maybeEmail?: string | null) =>
    (maybeEmail ?? "").trim().toLowerCase();
  const normalizePhone = (maybePhone?: string | null) =>
    (maybePhone ?? "").replace(/\D/g, "");

  const isSameReference = (ref: Reference) => {
    if (currentForm?.id && ref.id) return ref.id === currentForm.id;
    return ref === currentForm;
  };

  const normalizedEmail = normalizeEmail(email);
  const normalizedPhone = normalizePhone(phoneNumber);

  const hasDuplicateEmail =
    !!normalizedEmail &&
    value.some(
      (ref) =>
        !isSameReference(ref) && normalizeEmail(ref.email) === normalizedEmail
    );

  const hasDuplicatePhone =
    !!normalizedPhone &&
    value.some(
      (ref) =>
        !isSameReference(ref) && normalizePhone(ref.phoneNumber) === normalizedPhone
    );
  const isClientReference = referenceType === ReferenceType.Client;
  const requiresBusinessDetails = referenceType !== ReferenceType.Client;
  const duplicateWarning = hasDuplicateEmail
    ? "A reference with this email already exists."
    : hasDuplicatePhone
    ? "A reference with this phone number already exists."
    : "";

  const resetForm = () => {
    setReferenceType(undefined);
    setBusinessName("");
    setFirstname("");
    setLastName("");
    setTitle("");
    setProjectType(undefined);
    setEmail("");
    setPhoneNumber("");
    setStartDate("");
    setEndDate("");
    setYearsKnown("");
    setFormInstance((prev) => prev + 1);
  };

  const isValid =
    referenceType &&
    firstName &&
    firstName.trim() !== "" &&
    lastName &&
    lastName.trim() !== "" &&
    (!requiresBusinessDetails ||
      (businessName &&
        businessName.trim() !== "" &&
        title &&
        title.trim() !== "")) &&
    (isClientReference ? !!projectType : true) &&
    email &&
    email.trim() !== "" &&
    isEmailValid(email) &&
    phoneNumber &&
    phoneNumber.trim() !== "" &&
    isPhoneNumberValid(phoneNumber) &&
    // startDate &&
    // endDate &&
    // isDateBeforeToday(startDate) &&
    // isDateBeforeToday(endDate) &&
    // isDateBeforeDate(startDate, endDate) &&
    yearsKnown !== "" &&
    yearsKnown.trim() !== "" &&
    !hasDuplicateEmail &&
    !hasDuplicatePhone;

  const validations = {
    referenceType: !referenceType ? "Reference type is required" : false,
    businessName:
      requiresBusinessDetails &&
      (!businessName || businessName.trim() === "")
        ? "Business name is required"
        : false,
    firstName: !firstName || firstName.trim() === "" ? "First name is required" : false,
    lastName: !lastName || lastName.trim() === "" ? "Last name is required" : false,
    title:
      requiresBusinessDetails && (!title || title.trim() === "")
        ? "Title is required"
        : false,
    projectType:
      isClientReference && !projectType
        ? "Project type is required"
        : false,
    email: !email || email.trim() === ""
      ? "Email is required"
      : !isEmailValid(email)
      ? "Email is invalid"
      : hasDuplicateEmail
      ? "This email is already used for another reference"
      : false,
    phoneNumber: !phoneNumber || phoneNumber.trim() === ""
      ? "Phone number is required"
      : !isPhoneNumberValid(phoneNumber)
      ? "Phone number is invalid"
      : hasDuplicatePhone
      ? "This phone number is already used for another reference"
      : false,
    // startDate: !startDate
    //   ? "Start date is required"
    //   : !isDateBeforeToday(startDate)
    //   ? "Start date must be before today"
    //   : false,
    // endDate:
    //   !endDate && !currentForm
    //     ? "End date is required"
    //     : !isDateBeforeToday(endDate)
    //     ? "End date must be before today"
    //     : !isDateBeforeDate(startDate, endDate)
    //     ? "End date must be after start date"
    //     : false,
    yearsKnown:
      yearsKnown === "" || yearsKnown.trim() === ""
        ? "Field is required"
        : false,
  };

  const handleSave = () => {
    if (!isValid) return;

    const nextReference = {
      ProjectType: isClientReference
        ? (projectType as ReferenceProjectType)
        : undefined,
      businessName: requiresBusinessDetails ? businessName : "",
      firstName: firstName as string,
      lastName: lastName as string,
      title: requiresBusinessDetails ? title : "",
      type: referenceType as ReferenceType,
      email: email as string,
      phoneNumber: phoneNumber as string,
      startDate: startDate as string,
      endDate: endDate as string,
      yearsKnown: parseInt(yearsKnown) || 0,
    };

    if (currentForm && currentForm.id) {
      // Editing existing reference
      const updatedReferences = value.map((reference: Reference) =>
        reference.id === currentForm.id
          ? {
              ...reference,
              ...nextReference,
            }
          : reference
      );
      onChange(updatedReferences);
      resetForm();
      setCurrentForm && setCurrentForm(null);
    } else {
      // Adding new reference
      const newReference = {
        id: Date.now().toString(),
        ...nextReference,
      };
      const updatedReferences = [...value, newReference];
      onChange(updatedReferences);
      resetForm();
      setCurrentForm && setCurrentForm(null);
    }
  };

  const showValidationText = (validationText: string | boolean) => {
    if (typeof validationText === "string") {
      return validationText;
    }
    return undefined;
  };

  const keyByInstance = (key: string) => `${key}-${formInstance}`;

  return (
    <Stack direction={"column"} spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <DropdownSelect
          key={keyByInstance("referenceType")}
          options={ReferenceTypeOptions}
          value={referenceType}
          onChange={setReferenceType}
          label={"Reference Type"}
          isValid={!!!validations.referenceType}
          validationText={showValidationText(validations.referenceType)}
        />
        {requiresBusinessDetails && (
          <HdgTextInput
            key={keyByInstance("businessName")}
            label={"Business Name"}
            value={businessName}
            onChange={setBusinessName}
            isValid={!!!validations.businessName}
            validationText={showValidationText(validations.businessName)}
          />
        )}
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <HdgTextInput
          key={keyByInstance("firstName")}
          label={"First Name"}
          value={firstName}
          onChange={setFirstname}
          isValid={!!!validations.firstName}
          validationText={showValidationText(validations.firstName)}
        />
        <HdgTextInput
          key={keyByInstance("lastName")}
          label={"Last Name"}
          value={lastName}
          onChange={setLastName}
          isValid={!!!validations.lastName}
          validationText={showValidationText(validations.lastName)}
        />
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        {requiresBusinessDetails && (
          <HdgTextInput
            key={keyByInstance("title")}
            label={"Title"}
            value={title}
            onChange={setTitle}
            isValid={!!!validations.title}
            validationText={showValidationText(validations.title)}
          />
        )}
        {isClientReference && (
          <DropdownSelect
            key={keyByInstance("projectType")}
            options={ReferenceProjectTypeOptions}
            value={projectType}
            onChange={setProjectType}
            label={"Project Type"}
            isValid={!!!validations.projectType}
            validationText={showValidationText(validations.projectType)}
          />
        )}
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <HdgTextInput
          key={keyByInstance("email")}
          type="email"
          label={"Email"}
          value={email}
          onChange={setEmail}
          isValid={!!!validations.email}
          validationText={showValidationText(validations.email)}
        />
        <HdgTextInput
          key={keyByInstance("phoneNumber")}
          label={"Phone Number"}
          value={phoneNumber}
          onChange={(value) =>
            setPhoneNumber(
              isNaN(parseInt(value)) ? "" : parseInt(value).toString()
            )
          }
          isValid={!!!validations.phoneNumber}
          validationText={showValidationText(validations.phoneNumber)}
        />
      </Stack>

      {/* <Stack direction={"row"} spacing={2}>
        <HdgDatePicker
          key={keyByInstance("startDate")}
          label={"Start Date"}
          value={startDate}
          onChange={setStartDate}
          isValid={!!!validations.startDate}
          validationText={showValidationText(validations.startDate)}
        />
        <HdgDatePicker
          key={keyByInstance("endDate")}
          label={"End Date"}
          value={endDate}
          onChange={setEndDate}
          isValid={!!!validations.endDate}
          validationText={showValidationText(validations.endDate)}
        />
      </Stack> */}

      <HdgTextInput
        type="number"
        key={keyByInstance("yeaarsKnown")}
        label="How long have you known this person(years)?"
        value={yearsKnown}
        onChange={setYearsKnown}
        isValid={!!!validations.yearsKnown}
        validationText={showValidationText(validations.yearsKnown)}
      />

      <Button onClick={handleSave} variant="outlined" disabled={!isValid} sx={{ mb: 2 }}>
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          justifyContent="center"
        >
          <PlusIcon />

          <Typography variant="body2" component="span" fontWeight={600}>
            {currentForm ? "Save Reference" : "Add Reference"}
          </Typography>
        </Stack>
      </Button>
      {duplicateWarning && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mt: -1, mb: 2 }}
        >
          {duplicateWarning}
        </Typography>
      )}
    </Stack>
  );
};
